import { makeAutoObservable, runInAction } from 'mobx';
import i18n from 'i18n';
import { decryptCardData, generateRSAKeyPair } from 'components/Cards/utils';
import { BACKEND_MODULE_TYPES, CARD_AUTHORIZATIONS_STATUSES } from 'components/common/constants';
import {
  extractAccountNumbers,
  getFromDate,
  PREDEFINED_FILTERS
} from 'components/Cards/components/CardsAuthorizations/components/utils';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from 'services/authUtils';
import {
  blockCardRequest,
  confirmCardCreation,
  createCardRequest,
  forceChangeConfirmationTypeRequest,
  getCardAuthorizationsListRequest,
  getCardCVVRequest,
  getCardNumberRequest,
  getCardsList,
  replaceCardRequest,
  resendSecurityCode,
  unblockCardRequest
} from 'services/requestAgent';
import { convertDateToServerFilter } from 'services/utils';

const getFilters = (rangeFilter) => {
  const { from, to } = getFromDate(rangeFilter);
  return {
    from_date: new Date(from) || null,
    to_date: new Date(to) || null,
    wallets: [],
    statuses: CARD_AUTHORIZATIONS_STATUSES
  };
};

class CardsStore {
  isInitialized = false;
  isLoading = false;
  isAuthorizationsLoading = false;
  isShowingInternalActionConfirmation = false;
  isShowSwitchConfirmationMethodScreen = false;
  isCardCreated = false;
  confirmationId = null;
  error = null;
  temporaryError = null;
  accounts = [];
  cards = [];
  selectedCard = null;
  authorizationsList = [];
  selectedAccount = null;
  topUpAccount = null;
  cardNumber = null;
  cvv = null;

  totalElements = null;
  totalPages = null;
  rangeFilter = PREDEFINED_FILTERS.WEEK;
  filters = getFilters(this.rangeFilter);

  pagination = {
    size: 7,
    page: 0
  };
  latestAuthorizationsSort = {
    sort_column: 'updated_at',
    sort_direction: 'DESC',
    size: 5
  };

  authorizationsSort = {
    sort_column: 'updated_at',
    sort_direction: 'DESC'
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetCardsStore() {
    this.isInitialized = false;
    this.isLoading = false;
    this.isAuthorizationsLoading = false;
    this.isShowingInternalActionConfirmation = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.isCardCreated = false;
    this.confirmationId = null;
    this.error = null;
    this.accounts = [];
    this.cards = [];
    this.selectedCard = null;
    this.authorizationsList = [];
    this.selectedAccount = null;
    this.topUpAccount = null;
    this.cardNumber = null;
    this.cvv = null;
    this.totalElements = null;
    this.totalPages = null;
    this.rangeFilter = PREDEFINED_FILTERS.WEEK;
    this.filters = getFilters(this.rangeFilter);

    this.pagination = {
      size: 7,
      page: 0
    };
    this.latestAuthorizationsSort = {
      sort_column: 'updated_at',
      sort_direction: 'DESC',
      size: 5
    };
    this.authorizationsSort = {
      sort_column: 'updated_at',
      sort_direction: 'DESC'
    };
  }

  resetCardData() {
    this.cardNumber = null;
    this.cvv = null;
  }

  async initializeStoreState(customerNumber, accounts) {
    try {
      if (accounts.length === 0) {
        runInAction(() => {
          this.isInitialized = true;
        });
      } else {
        runInAction(() => {
          this.isLoading = true;
          this.accounts = accounts;
          if (!this.selectedAccount) {
            this.selectedAccount = accounts[0];
          }
        });

        const res = await getCardsList(customerNumber);

        runInAction(() => {
          this.cards = res;
          this.isInitialized = true;
        });
      }
    } catch (err) {
      this.error = err;
    } finally {
      this.isLoading = false;
    }
  }

  setIsLoading(status) {
    this.isLoading = status;
  }

  setSelectedCard(card) {
    this.selectedCard = card;
  }

  setIsCardCreated(status) {
    this.isCardCreated = status;
  }

  setIsShowingInternalActionConfirmation(status) {
    this.isShowingInternalActionConfirmation = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setError(error) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  setTemporaryError(error) {
    this.temporaryError = error;
    if (error) {
      // Clear temporary error after 5 seconds
      setTimeout(() => {
        runInAction(() => {
          if (this.temporaryError?.code === error?.code) {
            this.temporaryError = null;
          }
        });
      }, 5000);
    }
  }

  setSelectedAccount(account) {
    this.selectedAccount = account;
  }

  setTopUpAccount(account) {
    this.topUpAccount = account;
  }

  setFilter(fieldName, value) {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  }

  setRangeFilter(filterType, dateRange) {
    this.pagination.page = 0;
    this.rangeFilter = filterType;
    this.filters.from_date = new Date(dateRange.from);
    this.filters.to_date = new Date(dateRange.to);
  }

  clearPredefinedFilter() {
    this.rangeFilter = null;
  }

  clearFilters() {
    this.rangeFilter = PREDEFINED_FILTERS.WEEK;
    this.filters = getFilters(this.rangeFilter);
  }

  setAuthorizationsPage(page) {
    this.pagination.page = page;
  }

  setAuthorizationsPageSize(size) {
    this.pagination.size = size;
  }

  setAuthorizationsSort(sortData) {
    this.authorizationsSort.sort_column = sortData.sort_column;
    this.authorizationsSort.sort_direction = sortData.sort_direction;
  }

  getCardById(cardId) {
    return this.cards.find((card) => card?.card_id === cardId);
  }

  async sendCreateCardRequest(customerNumber, data) {
    try {
      this.setIsLoading(true);

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await createCardRequest(customerNumber, data);

      updateConfirmationActionType(confirmationType);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
      });
    } catch (err) {
      this.error = err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async sendConfirmCardCreationCode(customerNumber, code) {
    try {
      this.setIsLoading(true);

      const res = await confirmCardCreation(customerNumber, { confirmation_id: this.confirmationId, code });

      runInAction(() => {
        this.cards.push(res);
        this.isShowingInternalActionConfirmation = false;
        this.confirmationId = null;
        this.isCardCreated = true;
      });
    } catch (err) {
      // Handle push notification rejection
      if (err?.code === 'CONFIRMATION_REJECTED') {
        this.error = err;
        this.isShowingInternalActionConfirmation = false;
        // Reset to card creation form state
        this.confirmationId = null;
        this.error = {
          code: 'CONFIRMATION_REJECTED',
          message: i18n.getMessage
            ? i18n.getMessage('error.confirmationRejected')
            : 'Push notification was rejected. Please try again.'
        };
        // Show toast notification about rejection
        this.setTemporaryError({
          code: 'CONFIRMATION_REJECTED',
          message: i18n.getMessage
            ? i18n.getMessage('error.confirmationRejected')
            : 'Push notification was rejected. Please try again.'
        });
      } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
        // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
        this.error = err;
      }
    } finally {
      this.setIsLoading(false);
    }
  }

  async resendSecurityCode() {
    try {
      this.setIsLoading(true);

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.PAYMENT, this.confirmationId);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.error = null;
      });
    } catch (err) {
      this.error = err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async getCardNumber(customerNumber, cardId) {
    try {
      this.setIsLoading(true);

      const { publicKey, privateKey } = await generateRSAKeyPair();

      const { encrypted_card_data: encryptedCardData } = await getCardNumberRequest(customerNumber, cardId, {
        public_key: publicKey
      });

      const decryptedCardNumber = await decryptCardData(encryptedCardData, 'CardNumber', privateKey);

      runInAction(() => {
        this.cardNumber = decryptedCardNumber;
      });
    } catch (err) {
      this.error = err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async getCardCVV(customerNumber, cardId) {
    try {
      this.setIsLoading(true);

      const { publicKey, privateKey } = await generateRSAKeyPair();

      const { encrypted_card_data: encryptedCardData } = await getCardCVVRequest(customerNumber, cardId, {
        public_key: publicKey
      });

      const decryptedCardCVV = await decryptCardData(encryptedCardData, 'CVV2', privateKey);

      runInAction(() => {
        this.cvv = decryptedCardCVV;
      });
    } catch (err) {
      this.error = err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async blockCard(customerNumber, cardId) {
    try {
      this.setIsLoading(false);

      const cardInfo = await blockCardRequest(customerNumber, cardId);
      const cardIndex = this.cards.findIndex((card) => card?.card_id === cardId);

      runInAction(() => {
        this.cards[cardIndex] = cardInfo;
        this.selectedCard = cardInfo;
      });
    } catch (err) {
      this.error = err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async unblockCard(customerNumber, cardId) {
    try {
      this.setIsLoading(false);

      const cardInfo = await unblockCardRequest(customerNumber, cardId);
      const cardIndex = this.cards.findIndex((card) => card?.card_id === cardId);

      runInAction(() => {
        this.cards[cardIndex] = cardInfo;
        this.selectedCard = cardInfo;
      });
    } catch (err) {
      this.error = err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async replaceCard(customerNumber, cardId) {
    try {
      this.setIsLoading(false);

      const cardInfo = await replaceCardRequest(customerNumber, cardId);
      const cardIndex = this.cards.findIndex((card) => card?.card_id === cardId);

      runInAction(() => {
        if (cardIndex !== -1) {
          this.cards[cardIndex] = cardInfo;
        } else {
          this.cards = [...this.cards, cardInfo];
        }
      });
    } catch (err) {
      this.error = err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async createNewVirtualCard() {
    /* Create virtual card logic*/
  }

  async getLatestAuthorizationsList(customerNumber, filters = {}) {
    try {
      this.setIsLoading(true);

      const {
        content: data,
        total_elements,
        total_pages,
        number,
        size
      } = await getCardAuthorizationsListRequest(customerNumber, {
        ...this.latestAuthorizationsSort,
        ...filters,
        statuses: CARD_AUTHORIZATIONS_STATUSES
      });

      runInAction(() => {
        this.totalElements = total_elements;
        this.totalPages = total_pages;
        this.pagination.size = size;
        this.pagination.page = number;
        this.authorizationsList = data;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  }

  async getCardAuthorizations(customerNumber) {
    try {
      this.setIsLoading(true);
      const query = {
        ...this.filters,
        ...this.pagination,
        from: convertDateToServerFilter(this.filters.from_date),
        to: convertDateToServerFilter(this.filters.to_date),
        ...this.authorizationsSort,
        wallets: this.filters.wallets.length === 0 ? extractAccountNumbers(this.accounts) : this.filters.wallets
      };

      const {
        content: data,
        total_elements,
        total_pages,
        number,
        size
      } = await getCardAuthorizationsListRequest(customerNumber, query);

      runInAction(() => {
        this.totalElements = total_elements;
        this.totalPages = total_pages;
        this.pagination.size = size;
        this.pagination.page = number;
        this.authorizationsList = data;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      throw err;
    } finally {
      this.setIsLoading(false);
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(this.confirmationId, confirmationType, BACKEND_MODULE_TYPES.PAYMENT);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };
}

export default new CardsStore();
